/**
 * PostCard — single feed post card. Renders a shared FeedPost (live API post
 * or the anonymous static teaser). Like is wired via FeedContext; every action
 * passes through the auth gate first so guests are prompted to sign in.
 */
import {
  memo,
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type MouseEvent,
  type SyntheticEvent,
} from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { POST_FEELINGS, POST_IMAGE_LAYOUT } from '@wlb/shared/constants';
import { isPostOwnedByUser } from '@wlb/shared/utils/postOwnership';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';
import { getFirstName, getTaggedBuddiesParts } from '@wlb/shared/utils/postDisplay';
import { canSharePost } from '@wlb/shared/utils/postSharing';
import { getFullImageUrl } from '@wlb/shared/utils/imageUrl';
import type { FeedPost } from '@wlb/shared/types/post';
import { useAuthGate } from '../../components/feedback/AuthGate';
import { CommentIcon, HeartIcon, PostMenuIcon, RobiIcon, ShareIcon } from '../../components/ui/icons';
import { MentionText } from '../../components/ui/MentionLink';
import { avatarFallback } from '../../components/ui/avatarFallback';
import '../../components/ui/avatar.css';
import { Hashtag } from '../../components/ui/Hashtag';
import { HtmlContent } from '../../components/ui/HtmlContent';
import { containsHtml } from '../../utils/sanitizeHtml';
import { buildMentionIndex } from '../../utils/mentions';
import { splitSeparateHashtags } from '../../utils/separateHashtags';
import { useRelativeTime } from '../../utils/useRelativeTime';
import { useCurrentProfile } from '../profile/useCurrentProfile';
import { useOpenUserProfile } from '../profile/useOpenUserProfile';
import { resolvePostAlbum, resolvePostImageUri } from './postMedia';

interface PostCardProps {
  post: FeedPost;
  onLike?: (postId: string) => void;
  onComment?: (post: FeedPost) => void;
  onShare?: (post: FeedPost) => void;
  onLikes?: (post: FeedPost) => void;
  onMediaClick?: (post: FeedPost, mediaIndex?: number) => void;
  onMenu?: (post: FeedPost, anchorRect: DOMRect) => void;
  /** Post Buddy tap handler — only provided for the WLB feed on own posts. */
  onRobiPress?: (post: FeedPost) => void;
  /** Post id currently loading a suggestion, to show the spinner. */
  robiLoadingPostId?: string | null;
}

/** Widest frame a landscape clip may use in the feed preview. */
const VIDEO_FRAME_LANDSCAPE_CAP = '16 / 9';

/**
 * Memoised: an infinite feed keeps growing, so without this every appended page
 * re-renders every card already on screen and the append lands as a long task
 * in the middle of a scroll. Unchanged posts keep their object identity through
 * the feed reducers and `useUserPosts`, so the shallow compare holds — callers
 * must pass stable handler props for it to be worth anything.
 */
function PostCardComponent({ post, onLike, onComment, onShare, onLikes, onMediaClick, onMenu, onRobiPress, robiLoadingPostId }: PostCardProps) {
  const { t } = useTranslation();
  const { requireAuth } = useAuthGate();
  const openUserProfile = useOpenUserProfile();
  const currentProfile = useCurrentProfile();
  const relativeTime = useRelativeTime();

  // Identities @mentions in this post can resolve to: its author, the people
  // tagged on it, and the signed-in member.
  const mentions = useMemo(
    () =>
      buildMentionIndex([
        {
          userGuid: post.profileUserGuid,
          username: post.username,
          firstName: post.firstName,
          displayName: post.firstName || post.username,
        },
        {
          userGuid: currentProfile?.userGuid,
          username: currentProfile?.userName,
          firstName: currentProfile?.firstName,
          displayName: currentProfile?.name,
        },
        ...(post.taggedBuddies ?? []).map(buddy => ({
          userGuid: buddy.profileUserGuid,
          username: buddy.name,
          displayName: buddy.name,
        })),
      ]),
    [post.profileUserGuid, post.username, post.firstName, post.taggedBuddies, currentProfile],
  );
  const profile = useCurrentProfile();

  const { initial: authorInitial, tone: authorTone } = avatarFallback({
    seed: post.profileUserGuid ?? post.userId,
    name: post.firstName,
    username: post.username,
  });

  const feelingMatch = post.feeling
    ? POST_FEELINGS.find(f => f.key === post.feeling)
    : undefined;
  const feelingDisplay = post.feeling
    ? feelingMatch
      ? `${feelingMatch.emoji} ${t(`home.createPost.feeling.${feelingMatch.key}`)}`
      : post.feeling
    : null;

  const avatarUrl = typeof post.avatar === 'string' && post.avatar.trim() ? post.avatar : null;
  const imageUri = resolvePostImageUri(post.image);
  // A gallery post shows every photo in the grid; `image` only carries the
  // primary one, so the grid has to come from the album.
  const galleryImages = useMemo(
    () =>
      post.imageLayout === POST_IMAGE_LAYOUT.GALLERY
        ? resolvePostAlbum({ image: post.image, images: post.images })
        : [],
    [post.image, post.images, post.imageLayout],
  );
  const videoUri = post.video?.uri ?? post.videoUrl ?? null;

  // Feed preview frame for video: post metadata when the API has it,
  // otherwise the real dimensions once the browser reads the metadata.
  const [videoDims, setVideoDims] = useState<{ w: number; h: number } | null>(
    () => {
      const w = post.videoWidth ?? post.video?.width;
      const h = post.videoHeight ?? post.video?.height;
      if (w && h) return { w, h };
      return null;
    },
  );
  // Portrait → 4:5, square → 1:1, landscape → its own ratio but never wider
  // than 16:9. Unknown dimensions fall back to 16:9 until metadata arrives.
  const videoAspectRatio = useMemo(() => {
    if (!videoDims) return VIDEO_FRAME_LANDSCAPE_CAP;
    const ratio = videoDims.w / videoDims.h;
    if (ratio < 0.95) return '4 / 5';
    if (ratio <= 1.05) return '1 / 1';
    return ratio > 16 / 9
      ? VIDEO_FRAME_LANDSCAPE_CAP
      : `${videoDims.w} / ${videoDims.h}`;
  }, [videoDims]);
  const handleVideoMetadata = (e: SyntheticEvent<HTMLVideoElement>) => {
    const el = e.currentTarget;
    if (el.videoWidth && el.videoHeight) {
      setVideoDims({ w: el.videoWidth, h: el.videoHeight });
    }
  };

  /**
   * Real dimensions of a single image, read once the browser has decoded it.
   * The API sends no image metadata, so this is the only source of the ratio.
   *
   * The dimensions carry the source they were measured from instead of being
   * cleared by an effect: a cached image fires `load` in the same commit that
   * swapped the source, before passive effects run, so a reset effect would
   * wipe the measurement that had just arrived and leave the picture at its
   * natural size inside the card. The ref covers the same case from the other
   * side, measuring an image that was already decoded before it mounted.
   */
  const [imageDims, setImageDims] = useState<{ uri: string; w: number; h: number } | null>(null);
  const measureImage = useCallback(
    (el: HTMLImageElement | null) => {
      if (!el || !imageUri || !el.complete || !el.naturalWidth || !el.naturalHeight) return;
      const next = { uri: imageUri, w: el.naturalWidth, h: el.naturalHeight };
      setImageDims(current =>
        current && current.uri === next.uri && current.w === next.w && current.h === next.h
          ? current
          : next,
      );
    },
    [imageUri],
  );

  /**
   * Width that lets the picture keep its own shape: the full card width while
   * that stays under the height cap, otherwise the width a height-capped image
   * needs. A portrait photo therefore ends up centred at its natural ratio
   * instead of letterboxed inside a full-width canvas.
   */
  const imageStyle = useMemo<CSSProperties | undefined>(() => {
    if (!imageDims || imageDims.uri !== imageUri) return undefined;
    const ratio = imageDims.w / imageDims.h;
    return { width: `min(100%, calc(var(--post-media-max-height) * ${ratio}))` };
  }, [imageDims, imageUri]);

  const authorGuid = normalizeUserGuid(post.profileUserGuid);
  const canOpenAuthor = Boolean(authorGuid);
  const handleOpenAuthor = () => requireAuth(() => openUserProfile(authorGuid));
  const handleOpenTaggedUser = (profileUserGuid?: string | null) => {
    const taggedGuid = normalizeUserGuid(profileUserGuid);
    if (!taggedGuid) return;
    requireAuth(() => openUserProfile(taggedGuid));
  };

  // Display the first name when the API carries it; otherwise extract one
  // from the username, or show the username as-is. Identity and routing still
  // use profileUserGuid — this label is purely visual.
  const displayName = post.firstName?.trim() || getFirstName(post.username) || post.username;

  const taggedParts = useMemo(
    () => getTaggedBuddiesParts(displayName, post.taggedBuddies, t),
    [displayName, post.taggedBuddies, t],
  );

  const handleLike = () => requireAuth(() => onLike?.(post.id));
  const handleComment = () => requireAuth(() => onComment?.(post));
  const handleShare = () => requireAuth(() => onShare?.(post));
  const handleMore = (event: MouseEvent<HTMLButtonElement>) => {
    requireAuth(() => onMenu?.(post, event.currentTarget.getBoundingClientRect()));
  };

  // Hashtags the user added via the tag selector are stored separately in
  // `post.hashtags` but appended to `post.text` by the API. Strip them from
  // the trailing text so the caption renders as typed and the tags go on a
  // new line below. Inline hashtags (typed in the caption) stay put.
  const { caption, separateHashtags } = useMemo(
    () => splitSeparateHashtags(post.text, post.hashtags),
    [post.text, post.hashtags],
  );

  const isOwnPost = isPostOwnedByUser(post, profile);
  const followedAgent = profile?.followedAiAgent;
  const buddyAvatarUrl = getFullImageUrl(followedAgent?.profilePicture) ?? null;
  const showPostBuddy = isOwnPost && Boolean(onRobiPress);
  const isRobiLoading = robiLoadingPostId === post.id;
  const handleRobiPress = () => requireAuth(() => onRobiPress?.(post));

  const avatarContent = avatarUrl ? (
    <img className="post-card__avatar" src={avatarUrl} alt="" />
  ) : (
    <span className="post-card__avatar" data-avatar-tone={authorTone} aria-hidden>
      {authorInitial}
    </span>
  );

  return (
    <article className="post-card">
      <header className="post-card__header">
        {canOpenAuthor ? (
          <button
            className="post-card__author-action post-card__avatar-action"
            type="button"
            aria-label={t('home.accessibility.openUserProfile', { username: post.username })}
            onClick={handleOpenAuthor}
          >
            {avatarContent}
          </button>
        ) : (
          avatarContent
        )}
        <div className="post-card__author">
          <span className="post-card__name">
            {canOpenAuthor ? (
              <button className="post-card__author-action" type="button" onClick={handleOpenAuthor}>
                {displayName}
              </button>
            ) : (
              displayName
            )}
            {feelingDisplay && (
              <span className="post-card__feeling">
                {' '}{t('home.feed.isFeeling', { feeling: feelingDisplay })}
              </span>
            )}
            {taggedParts.segments.map((segment, index) => {
              const taggedGuid = normalizeUserGuid(segment.profileUserGuid);
              const segmentKey =
                segment.type === 'bold'
                  ? `tagged-${taggedGuid ?? segment.value}-${index}`
                  : `tagged-text-${segment.value}-${index}`;

              if (segment.type === 'bold' && taggedGuid) {
                return (
                  <button
                    key={segmentKey}
                    className="post-card__author-action post-card__tagged-user"
                    type="button"
                    aria-label={t('home.accessibility.openUserProfile', {
                      username: segment.value,
                    })}
                    onClick={() => handleOpenTaggedUser(segment.profileUserGuid)}
                  >
                    {segment.value}
                  </button>
                );
              }

              return (
                <span
                  key={segmentKey}
                  className={
                    segment.type === 'bold'
                      ? undefined
                      : 'post-card__tagged-text'
                  }
                >
                  {segment.value}
                </span>
              );
            })}
          </span>
          <span className="post-card__meta">
            {post.currentWeight ? `${t('home.feed.cw')}${post.currentWeight} · ` : ''}
            {relativeTime(post.postDate, { fallback: post.timeAgo, naiveZone: 'local' })}
          </span>
        </div>
        <button
          className="post-card__menu-action"
          type="button"
          aria-label={t('home.rightBanner.more')}
          onClick={handleMore}
        >
          <PostMenuIcon size={18} />
        </button>
      </header>

      {caption.trim() && (
        containsHtml(caption) ? (
          <HtmlContent html={caption} className="post-card__text" mentions={mentions} />
        ) : (
          <p className="post-card__text"><MentionText text={caption} mentions={mentions} /></p>
        )
      )}

      {separateHashtags.length > 0 && (
        <p className="post-card__text post-card__separate-hashtags">
          {separateHashtags.map(tag => <Hashtag key={tag}>{tag}</Hashtag>)}
        </p>
      )}

      {videoUri ? (
        <button className="post-card__media-button" type="button" onClick={() => onMediaClick?.(post)} aria-label={t('home.feed.actions.openMedia')}>
          <video
            className="post-card__media"
            style={{ aspectRatio: videoAspectRatio }}
            src={videoUri}
            controls
            preload="metadata"
            onLoadedMetadata={handleVideoMetadata}
          />
        </button>
      ) : galleryImages.length >= 2 ? (
        <div
          className={`post-card__gallery post-card__gallery--${Math.min(galleryImages.length, 6)}`}
        >
          {galleryImages.slice(0, 6).map((src, index) => (
            <button
              key={index}
              className="post-card__gallery-item"
              type="button"
              onClick={() => onMediaClick?.(post, index)}
              aria-label={t('home.feed.actions.openMedia')}
            >
              <img src={src} alt="" loading="lazy" decoding="async" />
              {index === 5 && galleryImages.length > 6 && (
                <span className="post-card__gallery-overlay" aria-hidden="true">
                  +{galleryImages.length - 6}
                </span>
              )}
            </button>
          ))}
        </div>
      ) : imageUri ? (
        <button className="post-card__media-button" type="button" onClick={() => onMediaClick?.(post)} aria-label={t('home.feed.actions.openMedia')}>
          <img
            ref={measureImage}
            className={`post-card__media${imageStyle ? '' : ' post-card__media--unmeasured'}`}
            style={imageStyle}
            src={imageUri}
            alt=""
            loading="lazy"
            decoding="async"
            onLoad={event => measureImage(event.currentTarget)}
          />
        </button>
      ) : null}

      <footer className="post-card__actions">
        <div className="post-card__action-group">
          <button
            className={`post-card__action${post.liked ? ' post-card__action--liked' : ''}`}
            onClick={handleLike}
            aria-pressed={post.liked}
            aria-label={t('home.feed.actions.like')}
          >
            <HeartIcon size={20} fill={post.liked ? 'currentColor' : 'none'} />
          </button>
          {post.likes > 0 && <button className="post-card__count" type="button" onClick={() => requireAuth(() => onLikes?.(post))} aria-label={t('home.feed.actions.viewLikes')}>{post.likes}</button>}
        </div>
        <button
          className="post-card__action"
          onClick={handleComment}
          aria-label={`${t('home.feed.actions.comment')} (${post.comments})`}
        >
          <CommentIcon size={20} />
          <span>{post.comments}</span>
        </button>
        {canSharePost(post) ? (
          <button
            className="post-card__action"
            onClick={handleShare}
            aria-label={`${t('home.feed.actions.share')} (${post.shares})`}
          >
            <ShareIcon size={20} />
            <span>{post.shares}</span>
          </button>
        ) : null}
        {showPostBuddy && (
          <button
            className="post-card__buddy"
            type="button"
            disabled={isRobiLoading}
            aria-label={t(isRobiLoading ? 'home.feed.postBuddy.iconLabelLoading' : 'home.feed.postBuddy.iconLabel')}
            aria-busy={isRobiLoading}
            onClick={handleRobiPress}
          >
            {isRobiLoading ? (
              <span className="post-card__buddy-spinner" aria-hidden />
            ) : buddyAvatarUrl ? (
              <img className="post-card__buddy-avatar" src={buddyAvatarUrl} alt="" aria-hidden />
            ) : (
              <RobiIcon size={24} />
            )}
          </button>
        )}
      </footer>
    </article>
  );
}

export const PostCard = memo(PostCardComponent);
